/* eslint-disable no-unused-expressions */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTrackedState } from '../store';
import moment from 'moment';
import { getChatTeam } from '../../Auth';

const GROUP_PAGE_LIMIT = 20;
const DUO_PAGE_LIMIT = 10;
const MESSAGE_PAGE_LIMIT = 30;

const MESSAGE_SEARCH_LOOKBACK_DAYS = 185;
const MIN_MESSAGE_QUERY_LENGTH = 2;

function isTimeoutError(err) {
    const status = err?.status || err?.response?.status || err?.StatusCode;
    if (status === 504) return true;
    return typeof err?.message === 'string' && err.message.toLowerCase().includes('timeout');
}

// Derive display name for any channel type
function getChannelDisplayName(ch, currentUserId) {
    const type = ch.type || ch.data?.type;
    if (type === 'duo') {
        const members = Object.values(ch.state?.members || {});
        const others = members.filter(m => m.user_id !== currentUserId);
        if (others.length === 0) return ch.data?.name || 'You';
        if (others.length === 1) return others[0].user?.name || 'Unknown';
        return others.map(m => m.user?.name || 'Unknown').join(', ');
    }
    return ch.data?.name || ch.name || 'Unknown';
}

function getChannelSubLabel(ch, currentUserId) {
    const type = ch.type || ch.data?.type;
    if (type === 'duo') {
        const members = Object.values(ch.state?.members || {});
        const others = members.filter(m => m.user_id !== currentUserId);
        if (others.length === 1) {
            const title = others[0].user?.title || others[0].user?.user_role;
            return title || 'Direct message';
        }
        return `${members.length} members`;
    }
    if (type === 'group') {
        const count = ch.data?.member_count || Object.keys(ch.state?.members || {}).length;
        return `Group · ${count} members`;
    }
    if (type === 'bot') return 'Bot';
    return '';
}

function highlight(text, query) {
    if (!query || !text) return text;
    const esc = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${esc})`, 'gi'));
    return parts.map((p, i) =>
        p.toLowerCase() === query.toLowerCase()
            ? <mark key={i} style={{ background: 'rgba(0,113,188,0.12)', color: '#0071BC', borderRadius: 2, padding: '0 1px', fontWeight: 600 }}>{p}</mark>
            : p
    );
}

function initialsFor(name = '') {
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
}

function ChannelAvatar({ ch, currentUserId, size = 36 }) {
    const type = ch.type || ch.data?.type;
    const name = getChannelDisplayName(ch, currentUserId);
    const isGroup = type === 'group';
    let online = false;
    if (type === 'duo') {
        const members = Object.values(ch.state?.members || {});
        const other = members.find(m => m.user_id !== currentUserId);
        online = other?.user?.online || false;
    }
    return (
        <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
            <div style={{
                width: size, height: size,
                borderRadius: '50%',
                background: '#0071BC',
                color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: size > 32 ? 13 : 11, fontWeight: 600,
                userSelect: 'none',
            }}>
                {isGroup ? initialsFor(ch.data?.name ?? '') : initialsFor(name)}
            </div>
            {type === 'duo' && online && (
                <div style={{ position: 'absolute', bottom: 0, right: 0, width: 10, height: 10, background: '#22c55e', borderRadius: '50%', border: '2px solid #fff' }} />
            )}
        </div>
    );
}

function ChannelRow({ ch, query, onClick, currentUserId }) {
    const displayName = getChannelDisplayName(ch, currentUserId);
    const subLabel = getChannelSubLabel(ch, currentUserId);
    return (
        <div
            role="button"
            tabIndex={0}
            onClick={() => onClick(ch)}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', cursor: 'pointer', borderRadius: 8, margin: '0 6px' }}
            onMouseEnter={e => e.currentTarget.style.background = '#F0F7FF'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick(ch);
                }
            }}
        >
            <ChannelAvatar ch={ch} currentUserId={currentUserId} size={36} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {query ? highlight(displayName, query) : displayName}
                </div>
                {subLabel && <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>{subLabel}</div>}
            </div>
        </div>
    );
}

function MessageRow({ msg, query, onClickMsg, loading }) {
    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onClickMsg}
            style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '8px 14px', cursor: loading ? 'wait' : 'pointer',
                opacity: loading ? 0.6 : 1, borderRadius: 8, margin: '0 6px',
            }}
            onMouseEnter={e => !loading && (e.currentTarget.style.background = '#F0F7FF')}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClickMsg();
                }
            }}
        >
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: '#111', lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {query ? highlight(msg.text, query) : msg.text}
                </div>
                <div style={{ fontSize: 11, marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                    {msg.user?.name && <span style={{ color: '#0071BC', fontWeight: 500 }}>{msg.user.name}</span>}
                    <span style={{ color: '#ccc' }}>·</span>
                    <span style={{ color: '#aaa' }}>{moment(msg.created_at).format('DD MMM YYYY')}</span>
                </div>
            </div>
            {loading && (
                <div style={{ width: 14, height: 14, border: '1.5px solid #eee', borderTopColor: '#0071BC', borderRadius: '50%', animation: 'spin 0.6s linear infinite', flexShrink: 0, marginTop: 2 }} />
            )}
        </div>
    );
}

// Section label 
function SectionLabel({ children }) {
    return (
        <div style={{
            fontSize: 10, fontWeight: 700, color: '#999',
            padding: '8px 14px 4px', textTransform: 'uppercase', letterSpacing: '0.08em',
        }}>
            {children}
        </div>
    );
}

function LoadMoreButton({ onClick, loading, label = 'Load more' }) {
    return (
        <div style={{ padding: '4px 14px 8px' }}>
            <button
                onClick={onClick}
                disabled={loading}
                style={{
                    width: '100%', padding: '7px 10px', fontSize: 12, fontWeight: 600,
                    color: '#0071BC', background: '#F0F7FF', border: '1px solid #DCEBFB',
                    borderRadius: 6, cursor: loading ? 'wait' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
            >
                {loading && (
                    <span style={{ width: 12, height: 12, border: '1.5px solid #cfe4fa', borderTopColor: '#0071BC', borderRadius: '50%', animation: 'spin 0.6s linear infinite', display: 'inline-block' }} />
                )}
                {loading ? 'Loading…' : label}
            </button>
        </div>
    );
}

// Main Search component
function Search({ onSelectChannel, setMsgId }) {
    const state = useTrackedState();
    const { chatClient, isOpen } = state;
    const currentUserId = chatClient?.userID;

    const [query, setQuery] = useState('');
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingMoreChannels, setLoadingMoreChannels] = useState(false);
    const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
    const [resolvingCid, setResolvingCid] = useState(null);
    const [messagesError, setMessagesError] = useState(null);

    const inputRef = useRef(null);
    const debounceRef = useRef(null);
    const abortRef = useRef(null);
    const channelLoadMoreAbortRef = useRef(null);
    const messageLoadMoreAbortRef = useRef(null);
    const searchCache = useRef(new Map());
    const resultsQueryRef = useRef('');

    useEffect(() => { inputRef.current?.focus(); }, []);

    const resetSearch = useCallback(() => {
        clearTimeout(debounceRef.current);
        abortRef.current?.abort();
        channelLoadMoreAbortRef.current?.abort();
        messageLoadMoreAbortRef.current?.abort();
        setLoading(false);
        setLoadingMoreChannels(false);
        setLoadingMoreMessages(false);
        setMessagesError(null);
        setQuery('');
        setResults(null);
        resultsQueryRef.current = '';
    }, []);

    useEffect(() => {
        if (query && !isOpen) {
            // clear searc result on chat close
            resetSearch();
            inputRef.current?.focus();
        }
    }, [isOpen, query, resetSearch])

    useEffect(() => {
        return () => {
            clearTimeout(debounceRef.current);
            abortRef.current?.abort();
            channelLoadMoreAbortRef.current?.abort();
            messageLoadMoreAbortRef.current?.abort();
        };
    }, []);

    // Fetches one page of group + duo channels at the given offsets.
    const fetchChannelsPage = useCallback(async (q, groupOffset, duoOffset, signal) => {
        const [groupChannelRes, duoChannelRes] = await Promise.all([
            chatClient.queryChannels(
                {
                    type: 'group',
                    name: { $autocomplete: q },
                    members: { $in: [currentUserId] },
                    teams: { $in: [getChatTeam()] }
                },
                { last_message_at: -1 },
                { limit: GROUP_PAGE_LIMIT, offset: groupOffset, state: true }
            ),
            chatClient.queryChannels(
                {
                    type: 'duo',
                    members: { $in: [currentUserId] },
                    'member.user.name': { $autocomplete: q },
                    teams: { $in: [getChatTeam()] }
                },
                { last_message_at: -1 },
                { limit: DUO_PAGE_LIMIT, offset: duoOffset, state: true }
            )
        ]);
        if (signal?.aborted) return null;
        return { groupChannelRes: groupChannelRes ?? [], duoChannelRes: duoChannelRes ?? [] };
    }, [chatClient, currentUserId]);

    const fetchMessagesPage = useCallback(async (q, next, signal) => {
        if (q.trim().length < MIN_MESSAGE_QUERY_LENGTH) return { messages: [], next: null };

        // Cutoff date ~6 months (185 days) before now,
        const lookbackCutoff = new Date(Date.now() - MESSAGE_SEARCH_LOOKBACK_DAYS * 24 * 60 * 60 * 1000).toISOString();

        const messageRes = await chatClient.search(
            { members: { $in: [currentUserId] }, teams: { $in: [getChatTeam()] } },
            {
                text: { $q: q },
                created_at: { $gte: lookbackCutoff },
            },
            {
                limit: MESSAGE_PAGE_LIMIT,
                ...(next ? { next } : {}),
            }
        );
        if (signal?.aborted) return null;
        return {
            messages: messageRes?.results?.map(r => r.message) ?? [],
            next: messageRes?.next ?? null,
        };
    }, [chatClient, currentUserId]);

    const doSearch = useCallback(async (q) => {
        if (!q.trim()) { setResults(null); setLoading(false); return; }
        if (searchCache.current.has(q)) {
            setResults(searchCache.current.get(q));
            resultsQueryRef.current = q;
            setMessagesError(null);
            setLoading(false);
            return;
        }
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true);
        setMessagesError(null);
        try {
            const [channelsSettled, messagesSettled] = await Promise.allSettled([
                fetchChannelsPage(q, 0, 0, controller.signal),
                fetchMessagesPage(q, null, controller.signal),
            ]);
            if (controller.signal.aborted) return;

            if (channelsSettled.status === 'rejected') {
                console.error('Channel search failed:', channelsSettled.reason);
            }
            const channelsPage = channelsSettled.status === 'fulfilled' ? channelsSettled.value : null;
            const { groupChannelRes, duoChannelRes } = channelsPage ?? { groupChannelRes: [], duoChannelRes: [] };

            let messages = [];
            let nextCursor = null;
            let msgError = null;
            if (messagesSettled.status === 'fulfilled') {
                messages = messagesSettled.value?.messages ?? [];
                nextCursor = messagesSettled.value?.next ?? null;
            } else {
                console.error('Message search failed:', messagesSettled.reason);
                msgError = isTimeoutError(messagesSettled.reason) ? 'timeout' : 'error';
            }

            const data = {
                channels: [...groupChannelRes, ...duoChannelRes],
                messages,
                channelPaging: {
                    groupOffset: groupChannelRes.length,
                    duoOffset: duoChannelRes.length,
                    hasMoreGroup: groupChannelRes.length === GROUP_PAGE_LIMIT,
                    hasMoreDuo: duoChannelRes.length === DUO_PAGE_LIMIT,
                },
                messagePaging: {
                    next: nextCursor,
                    hasMore: !msgError && !!nextCursor,
                },
            };

            if (!msgError) searchCache.current.set(q, data);
            setResults(data);
            setMessagesError(msgError);
            resultsQueryRef.current = q;
        } catch (err) {
            if (!controller.signal.aborted) console.error(err);
        } finally {
            if (!controller.signal.aborted) setLoading(false);
        }
    }, [fetchChannelsPage, fetchMessagesPage]);

    const loadMoreChannels = useCallback(async () => {
        if (!results || loadingMoreChannels) return;
        const q = resultsQueryRef.current;
        const { groupOffset, duoOffset, hasMoreGroup, hasMoreDuo } = results.channelPaging;
        if (!hasMoreGroup && !hasMoreDuo) return;

        channelLoadMoreAbortRef.current?.abort();
        const controller = new AbortController();
        channelLoadMoreAbortRef.current = controller;
        setLoadingMoreChannels(true);
        try {
            const [groupChannelRes, duoChannelRes] = await Promise.all([
                hasMoreGroup
                    ? chatClient.queryChannels(
                        { type: 'group', name: { $autocomplete: q }, members: { $in: [currentUserId] }, teams: { $in: [getChatTeam()] } },
                        { last_message_at: -1 },
                        { limit: GROUP_PAGE_LIMIT, offset: groupOffset, state: true }
                    )
                    : Promise.resolve([]),
                hasMoreDuo
                    ? chatClient.queryChannels(
                        { type: 'duo', members: { $in: [currentUserId] }, 'member.user.name': { $autocomplete: q }, teams: { $in: [getChatTeam()] } },
                        { last_message_at: -1 },
                        { limit: DUO_PAGE_LIMIT, offset: duoOffset, state: true }
                    )
                    : Promise.resolve([]),
            ]);
            if (controller.signal.aborted) return;
            const newGroup = groupChannelRes ?? [];
            const newDuo = duoChannelRes ?? [];
            setResults(prev => {
                if (!prev) return prev;
                const updated = {
                    ...prev,
                    channels: [...prev.channels, ...newGroup, ...newDuo],
                    channelPaging: {
                        groupOffset: prev.channelPaging.groupOffset + newGroup.length,
                        duoOffset: prev.channelPaging.duoOffset + newDuo.length,
                        hasMoreGroup: hasMoreGroup ? newGroup.length === GROUP_PAGE_LIMIT : false,
                        hasMoreDuo: hasMoreDuo ? newDuo.length === DUO_PAGE_LIMIT : false,
                    },
                };
                searchCache.current.set(q, updated);
                return updated;
            });
        } catch (err) {
            if (!controller.signal.aborted) console.error(err);
        } finally {
            if (!controller.signal.aborted) setLoadingMoreChannels(false);
        }
    }, [chatClient, currentUserId, results, loadingMoreChannels]);

    const loadMoreMessages = useCallback(async () => {
        if (!results || loadingMoreMessages) return;
        const q = resultsQueryRef.current;
        const { next, hasMore } = results.messagePaging;
        if (!hasMore) return;

        messageLoadMoreAbortRef.current?.abort();
        const controller = new AbortController();
        messageLoadMoreAbortRef.current = controller;
        setLoadingMoreMessages(true);
        setMessagesError(null);
        try {
            const page = await fetchMessagesPage(q, next, controller.signal);
            if (controller.signal.aborted || page === null) return;

            setResults(prev => {
                if (!prev) return prev;
                const updated = {
                    ...prev,
                    messages: [...prev.messages, ...page.messages],
                    messagePaging: { next: page.next, hasMore: !!page.next },
                };
                searchCache.current.set(q, updated);
                return updated;
            });
        } catch (err) {
            if (!controller.signal.aborted) {
                console.error(err);
                setMessagesError(isTimeoutError(err) ? 'timeout' : 'error');
            }
        } finally {
            if (!controller.signal.aborted) setLoadingMoreMessages(false);
        }
    }, [fetchMessagesPage, results, loadingMoreMessages]);

    const retryMessageSearch = useCallback(async () => {
        const q = resultsQueryRef.current;
        if (!q || loadingMoreMessages) return;
        messageLoadMoreAbortRef.current?.abort();
        const controller = new AbortController();
        messageLoadMoreAbortRef.current = controller;
        setLoadingMoreMessages(true);
        setMessagesError(null);
        try {
            const page = await fetchMessagesPage(q, null, controller.signal);
            if (controller.signal.aborted || page === null) return;
            setResults(prev => {
                if (!prev) return prev;
                const updated = {
                    ...prev,
                    messages: page.messages,
                    messagePaging: { next: page.next, hasMore: !!page.next },
                };
                searchCache.current.set(q, updated);
                return updated;
            });
        } catch (err) {
            if (!controller.signal.aborted) {
                console.error(err);
                setMessagesError(isTimeoutError(err) ? 'timeout' : 'error');
            }
        } finally {
            if (!controller.signal.aborted) setLoadingMoreMessages(false);
        }
    }, [fetchMessagesPage, loadingMoreMessages]);

    const handleInput = (e) => {
        const v = e.target.value;
        setQuery(v);
        clearTimeout(debounceRef.current);
        if (!v.trim()) {
            abortRef.current?.abort();
            channelLoadMoreAbortRef.current?.abort();
            messageLoadMoreAbortRef.current?.abort();
            setLoading(false);
            setLoadingMoreChannels(false);
            setLoadingMoreMessages(false);
            setMessagesError(null);
            setResults(null);
            resultsQueryRef.current = '';
            return;
        }
        setLoading(true);
        debounceRef.current = setTimeout(() => doSearch(v.trim()), 300);
    };

    const handleChannelClick = (ch) => onSelectChannel(ch);

    const handleMessageClick = async (msg) => {
        const cid = msg.cid;
        if (!cid || resolvingCid) return;
        const cached = results?.channels?.find(c => c.cid === cid);
        if (cached) {
            onSelectChannel(cached);
            setMsgId(msg?.id)
            return;
        }
        const localChannel = chatClient.activeChannels?.[cid];
        if (localChannel?.initialized) {
            onSelectChannel(localChannel);
            setMsgId(msg?.id)
            return;
        }
        setResolvingCid(cid);
        try {
            const [fullChannel] = await chatClient.queryChannels(
                { cid: { $eq: cid }, members: { $in: [currentUserId] } },
                {}, { limit: 1, state: true }
            );
            if (fullChannel) {
                onSelectChannel(fullChannel);
                setMsgId(msg?.id)
            }
        } catch (err) {
            console.error('Failed to resolve channel for message:', err);
        } finally {
            setResolvingCid(null);
        }
    };

    const msgByChannel = {};
    (results?.messages || []).forEach(m => {
        if (!msgByChannel[m.cid]) msgByChannel[m.cid] = [];
        msgByChannel[m.cid].push(m);
    });

    const hasResults = results && (results.channels.length || results.messages.length);
    const showResults = query.trim().length > 0;

    return (
        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%' }}>

            {/* Search input */}
            <div style={{ padding: '10px 12px', flexShrink: 0 }}>
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#F4F5F7',
                    border: '1.5px solid transparent', borderRadius: 24, transition: 'border-color 0.15s',
                }}
                    onFocusCapture={e => e.currentTarget.style.borderColor = '#0071BC'}
                    onBlurCapture={e => e.currentTarget.style.borderColor = 'transparent'}
                >
                    {loading
                        ? <div style={{ width: 16, height: 16, border: '1.5px solid #ddd', borderTopColor: '#0071BC', borderRadius: '50%', animation: 'spin 0.6s linear infinite', flexShrink: 0 }} />
                        : (
                            <svg viewBox="0 -0.5 25 25" height={16} width={16} fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                                <path fillRule="evenodd" clipRule="evenodd" d="M5.5 10.7655C5.50003 8.01511 7.44296 5.64777 10.1405 5.1113C12.8381 4.57483 15.539 6.01866 16.5913 8.55977C17.6437 11.1009 16.7544 14.0315 14.4674 15.5593C12.1804 17.0871 9.13257 16.7866 7.188 14.8415C6.10716 13.7604 5.49998 12.2942 5.5 10.7655Z" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M17.029 16.5295L19.5 19.0005" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        )
                    }
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={handleInput}
                        placeholder="Search people, groups, messages…"
                        style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: '#111', fontFamily: 'inherit' }}
                    />
                    {query && (
                        <button
                            onClick={() => { resetSearch(); inputRef.current?.focus(); }}
                            style={{ fontSize: 11, color: '#999', background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', lineHeight: 1, flexShrink: 0 }}
                            aria-label="Clear"
                        >
                            ✕
                        </button>
                    )}
                </div>
            </div>

            {/* Search results panel — visually separated card */}
            {showResults && (
                <div style={{
                    margin: '0 10px 10px', background: '#fff', border: '1.5px solid #E3EDF7', borderRadius: 5,
                    boxShadow: '0 4px 16px rgba(0,113,188,0.08)', overflow: 'hidden', flexShrink: 0,
                    maxHeight: 340, overflowY: 'auto', scrollbarWidth: 'thin'
                }}>
                    {/* Loading skeleton */}
                    {loading && !hasResults && (
                        <div style={{ padding: '16px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {[1, 2, 3].map(i => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#F0F0F0', flexShrink: 0 }} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ height: 11, background: '#F0F0F0', borderRadius: 4, width: '55%', marginBottom: 5 }} />
                                        <div style={{ height: 9, background: '#F6F6F6', borderRadius: 4, width: '35%' }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* No results */}
                    {!loading && !hasResults && (
                        <div style={{ padding: '24px 14px', textAlign: 'center' }}>
                            <div style={{ fontSize: 28, marginBottom: 6 }}>🔍</div>
                            <div style={{ fontSize: 13, color: '#666' }}>No results for <strong>"{query}"</strong></div>
                            <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>Try a different name or keyword</div>
                        </div>
                    )}

                    {/* People & Channels section */}
                    {!loading && results?.channels?.length > 0 && (
                        <div>
                            <SectionLabel>People &amp; Channels</SectionLabel>
                            {results.channels.map(ch => (
                                <ChannelRow
                                    key={ch.cid}
                                    ch={ch}
                                    query={query}
                                    onClick={handleChannelClick}
                                    currentUserId={currentUserId}
                                />
                            ))}
                            {(results.channelPaging.hasMoreGroup || results.channelPaging.hasMoreDuo) && (
                                <LoadMoreButton onClick={loadMoreChannels} loading={loadingMoreChannels} />
                            )}
                        </div>
                    )}

                    {/* Divider between sections */}
                    {!loading && results?.channels?.length > 0 && results?.messages?.length > 0 && (
                        <div style={{ height: 1, background: '#F0F4F8', margin: '4px 14px' }} />
                    )}

                    {/* Message search failed / timed out */}
                    {!loading && messagesError && (
                        <div style={{ padding: '10px 14px' }}>
                            <div style={{ fontSize: 12, color: '#B54708', background: '#FFF6ED', border: '1px solid #FEC84B', borderRadius: 6, padding: '8px 10px' }}>
                                {messagesError === 'timeout'
                                    ? 'Message search is taking too long. Try a more specific term.'
                                    : "Couldn't search messages right now."}
                                <button
                                    onClick={retryMessageSearch}
                                    disabled={loadingMoreMessages}
                                    style={{ marginLeft: 8, fontSize: 12, fontWeight: 600, color: '#B54708', background: 'none', border: 'none', textDecoration: 'underline', cursor: loadingMoreMessages ? 'wait' : 'pointer', padding: 0 }}
                                >
                                    {loadingMoreMessages ? 'Retrying…' : 'Retry'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Recent Messages section */}
                    {!loading && !messagesError && results?.messages?.length > 0 && (
                        <div>
                            <SectionLabel>Recent Messages</SectionLabel>
                            {results.messages.map(m => (
                                <MessageRow
                                    key={m.id}
                                    msg={m}
                                    query={query}
                                    onClickMsg={() => handleMessageClick(m)}
                                    loading={resolvingCid === m.cid}
                                />
                            ))}
                            {results.messagePaging.hasMore && (
                                <LoadMoreButton onClick={loadMoreMessages} loading={loadingMoreMessages} />
                            )}
                        </div>
                    )}

                    {/* Footer hint */}
                    {hasResults > 0 && (
                        <div style={{ padding: '6px 14px 8px', borderTop: '1px solid #F4F5F7', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 10, color: '#bbb' }}>
                                {(results?.channels?.length || 0) + (results?.messages?.length || 0)} result{((results?.channels?.length || 0) + (results?.messages?.length || 0)) !== 1 ? 's' : ''}
                            </span>
                        </div>
                    )}
                </div>
            )}

            {/* Subtle divider between search area and channel list */}
            {!showResults && (
                <div style={{ height: 1, background: '#EAECEF', margin: '0 0 4px' }} />
            )}
        </div>
    );
}

export default Search;